/**
 * Top-level React error boundary.
 *
 * Wraps the entire app so a render error doesn't blank the whole screen.
 * Reports to Sentry (if initialized) and shows a friendly Ukrainian
 * fallback with a "Try again" button.
 *
 * Per project convention (settings page architecture rule), this is an
 * arrow-function component.
 */

import React from "react";
import * as Sentry from "@sentry/react";

const FallbackUI = ({ error, resetError }) => {
  const handleReload = () => {
    // Hard reload picks up any new JS chunks after a deploy
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  return (
    <div
      role="alert"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        background: "#0a0a0a",
        color: "#f5f5f5",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}
    >
      <div style={{ maxWidth: 480, textAlign: "center" }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>😬</div>
        <h1 style={{ fontSize: 24, marginBottom: 12, fontWeight: 600 }}>
          Щось пішло не так
        </h1>
        <p style={{ fontSize: 15, lineHeight: 1.5, color: "#a3a3a3", marginBottom: 24 }}>
          Ми вже отримали сповіщення про помилку і працюємо над виправленням.
          Спробуй оновити сторінку — скоріш за все, все запрацює.
        </p>

        {process.env.NODE_ENV !== "production" && error && (
          <details
            style={{
              textAlign: "left",
              background: "#171717",
              border: "1px solid #2a2a2a",
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              fontSize: 12,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              color: "#f87171",
              maxHeight: 200,
              overflow: "auto",
            }}
          >
            <summary style={{ cursor: "pointer", color: "#fca5a5" }}>
              Dev details
            </summary>
            <pre style={{ margin: "8px 0 0", whiteSpace: "pre-wrap" }}>
              {error.toString()}
              {"\n"}
              {error.stack}
            </pre>
          </details>
        )}

        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <button
            onClick={resetError}
            style={{
              background: "transparent",
              color: "#f5f5f5",
              border: "1px solid #404040",
              borderRadius: 8,
              padding: "10px 20px",
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Спробувати ще раз
          </button>
          <button
            onClick={handleReload}
            style={{
              background: "#a78bfa",
              color: "#0a0a0a",
              border: "none",
              borderRadius: 8,
              padding: "10px 20px",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Оновити сторінку
          </button>
        </div>
      </div>
    </div>
  );
};

export const AppErrorBoundary = ({ children }) => (
  <Sentry.ErrorBoundary
    fallback={({ error, resetError }) => (
      <FallbackUI error={error} resetError={resetError} />
    )}
    showDialog={false}
  >
    {children}
  </Sentry.ErrorBoundary>
);

export default AppErrorBoundary;
